/**
 * Screen Wake Lock wrapper that keeps itself alive across
 * `visibilitychange` — the browser drops a wake lock whenever the tab
 * becomes hidden, so we re-request it when the user returns.
 */
export class WakeLockGuard {
  private sentinel: WakeLockSentinel | null = null;
  private wanted = false;
  private readonly onVisibility: () => void;

  constructor() {
    this.onVisibility = (): void => {
      if (!this.wanted) return;
      if (document.visibilityState === 'visible') void this.acquire();
    };
  }

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  }

  async request(): Promise<boolean> {
    if (!WakeLockGuard.isSupported()) return false;
    this.wanted = true;
    document.addEventListener('visibilitychange', this.onVisibility);
    return this.acquire();
  }

  private async acquire(): Promise<boolean> {
    try {
      const nav = navigator as Navigator & {
        wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> };
      };
      const sentinel = (await nav.wakeLock?.request('screen')) ?? null;
      this.sentinel = sentinel;
      return sentinel !== null;
    } catch (err) {
      console.warn('[wakelock] request failed', err);
      return false;
    }
  }

  async release(): Promise<void> {
    this.wanted = false;
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.sentinel) {
      try {
        await this.sentinel.release();
      } catch {
        /* already released */
      }
      this.sentinel = null;
    }
  }
}
